import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDistrict } from '@/hooks/useDistrict'
import { useRegion } from '@/hooks/useRegion'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { AppLayout } from '@/components/layout/AppLayout'
import { AdminOnly } from '@/components/AdminOnly'
import { GenerateCohortsButton } from '@/components/cohort/GenerateCohortsButton'
import { CohortList } from '@/components/cohort/CohortList'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { SettlementType } from '@/types'

const settlementTypeLabels: Record<SettlementType, string> = {
  rural: 'Rural',
  suburban: 'Suburban',
  urban: 'Urban',
}

const settlementTypeBadgeStyles: Record<SettlementType, string> = {
  rural: 'bg-green-100 text-green-800 border-green-200',
  suburban: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  urban: 'bg-blue-100 text-blue-800 border-blue-200',
}

function formatPopulation(population: number): string {
  return population.toLocaleString()
}

export default function DistrictDetail() {
  const { id } = useParams<{ id: string }>()
  const { district, isLoading: districtLoading, error: districtError } = useDistrict(id)
  const { region, isLoading: regionLoading } = useRegion(district?.region_id)
  const { nation, isLoading: nationLoading } = useNation(region?.nation_id)
  const { world, isLoading: worldLoading } = useWorld(nation?.world_id)

  const isLoading = districtLoading
  const error = districtError
  const isParentLoading = regionLoading || nationLoading || worldLoading

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}` })
    }
    if (region) {
      items.push({ label: region.name, href: `/regions/${region.id}` })
    }
    if (district) {
      items.push({ label: district.name })
    }
    return items
  }, [world, nation, region, district])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {districtError?.message === 'JSON object requested, multiple (or no) rows returned'
            ? 'District not found.'
            : 'Failed to load district. Please try again later.'}
        </div>
      )}

      {(isLoading || isParentLoading) && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="bg-muted h-8 w-48 animate-pulse rounded" />
            <div className="bg-muted h-6 w-20 animate-pulse rounded-full" />
          </div>
          <Card>
            <CardHeader>
              <div className="bg-muted h-5 w-32 animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-muted h-4 w-40 animate-pulse rounded" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!isLoading && !isParentLoading && !error && district && (
        <>
          {/* Header */}
          <div className="mb-8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{district.name}</h1>
              <Badge variant="outline" className={cn(settlementTypeBadgeStyles[district.settlement_type])}>
                {settlementTypeLabels[district.settlement_type]}
              </Badge>
            </div>
            {region && (
              <AdminOnly>
                <GenerateCohortsButton
                  districtId={district.id}
                  districtName={district.name}
                  districtPopulation={district.population}
                  settlementType={district.settlement_type}
                  regionType={region.region_type}
                />
              </AdminOnly>
            )}
          </div>

          {/* District Info Card */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">District Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground">Type</dt>
                  <dd className="font-medium">{settlementTypeLabels[district.settlement_type]}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Population</dt>
                  <dd className="font-medium">{formatPopulation(district.population)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Seat Count</dt>
                  <dd className="font-medium">{district.seat_count}</dd>
                </div>
                {region && (
                  <div>
                    <dt className="text-muted-foreground">Region</dt>
                    <dd className="font-medium">{region.name}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Cohort List */}
          <div className="border-t pt-6">
            <CohortList districtId={district.id} districtName={district.name} />
          </div>
        </>
      )}
    </AppLayout>
  )
}
